import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {MatMenuModule} from '@angular/material/menu';
import {MatButtonModule} from '@angular/material/button';
import * as XLSX from 'xlsx';
import {MsgService} from '../../../../shared/service/msg.service';
import { MethodsUtils } from '../../../../shared/util/methods.utils';

@Component({
  selector: 'app-anexo-lista-envio',
  standalone: true,
  imports: [
    MatButtonModule, MatMenuModule
  ],
  templateUrl: './anexo-lista-envio.component.html',
  styleUrl: './anexo-lista-envio.component.css'
})
export class AnexoListaEnvioComponent {

  @Input() anexoArquivo: any;
  @Input() podeEditar = true;
  @Input() tipoDisparo?: string;
  @Output() envioAnexo = new EventEmitter<any>();
  @Output() colunasComplementaresAtualizadas = new EventEmitter<string[]>();
  arrastandoArquivo = false;

  private readonly msgService = inject(MsgService);

  baixarPlanilhaExemplo() {
    const exemplo = [
      {
        nome: 'Email de exemplo',
        telefone: '5531999998888',
        email: 'email-teste@email.com',
        cpf: '00000000000'
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(exemplo);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contatos');
    XLSX.writeFile(workbook, 'planilha-exemplo.xlsx');
  }

  uploadArquivo(event: any) {
    const file: File = event.target?.files[0];
    if (!file) return;
    this.processarArquivo(file, event.target);
  }

  aoArrastarSobre(event: DragEvent): void {
    if (!this.podeEditar) return;
    event.preventDefault();
    event.stopPropagation();
    this.arrastandoArquivo = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  aoArrastarSair(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.arrastandoArquivo = false;
  }

  aoSoltarArquivo(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.arrastandoArquivo = false;
    if (!this.podeEditar) return;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processarArquivo(file);
    }
  }

  private processarArquivo(file: File, input?: HTMLInputElement): void {
    if (!/\.(xls|xlsx)$/i.test(file.name)) {
      this.msgService.msgAdvertencia('Selecione um arquivo Excel no formato .xls ou .xlsx.');
      if (input) input.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const fileContent = e.target.result;
      const data = new Uint8Array(fileContent);

      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (jsonData.length === 0) {
        this.msgService.msgAdvertencia('Arquivo vazio.');
        return;
      }

      const originalKeys = Object.keys(jsonData[0]);

      const normalizedKeyMap = new Map<string, string>();

      originalKeys.forEach(key => {
        normalizedKeyMap.set(key.trim().toLowerCase(), key);
      });

      const colNome = normalizedKeyMap.get('nome');
      const colTelefone = normalizedKeyMap.get('telefone');
      const colEmail = normalizedKeyMap.get('email');
      const colCpf = normalizedKeyMap.get('cpf');

      if (!colNome || !colTelefone || !colEmail || !colCpf) {
        this.msgService.msgAdvertencia(
          'Arquivo deve conter as colunas: nome, telefone, email e cpf/cnpj.'
        );
        return;
      }

      const colunasComplementares = originalKeys.filter(key =>
        !['nome', 'telefone', 'email', 'cpf'].includes(key.trim().toLowerCase())
      );

      const base64Data = btoa(
        new Uint8Array(fileContent)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const telefonesSet = new Set<string>();

      const listaContatos = jsonData.map(row => {

        const telefone = this.normalizarTelefone(String(row[colTelefone] || '').trim());

        const isRepetido = telefonesSet.has(telefone);

        if (!isRepetido && telefone) {
          telefonesSet.add(telefone);
        }

        const camposComplementares = colunasComplementares.map(coluna => ({
        nome: coluna,
        valor: String(row[coluna] || '').trim()
        }));

        return {
          nomeContato: String(row[colNome] || '').trim(),
          telefone,
          email: String(row[colEmail] || '').trim(),
          cpf: String(row[colCpf] || '').trim(),

          camposComplementares,

          isContatoValido: this.validarTelefone(telefone),
          isEmailValido: undefined,
          isCpfValido: this.validarCpf(
            String(row[colCpf] || '').trim()
          ),
          isContatoRepetido: isRepetido,
          status: null
        };
      });

      this.anexoArquivo = {
        base64: base64Data,
        nomeArquivo: file.name,
        extensaoArquivo: file.name.split('.').pop(),
        colunasComplementares,
        formData: (() => {
          const formData = new FormData();
          formData.append('file', file);
          return formData;
        })(),
        listaContatos
      };

      this.envioAnexo.emit(this.anexoArquivo);
      this.colunasComplementaresAtualizadas.emit(colunasComplementares);

      if (input) input.value = '';
    };

    reader.readAsArrayBuffer(file);
  }

  validarTelefone(telefone: string): boolean {
    const telefoneNumerico = telefone.replace(/\D/g, '');
    if (this.tipoDisparo === 'sms') {
      return /^55\d{2}9\d{8}$/.test(telefoneNumerico);
    }
    if (telefoneNumerico.length >= 12 && telefoneNumerico.length <= 13) {
      return true;
    }
    return false;
  }

  private normalizarTelefone(telefone: string): string {
    if (this.tipoDisparo !== 'sms') return telefone;
    let digitos = telefone.replace(/\D/g, '');
    if (digitos.length === 10) {
      digitos = `55${digitos.slice(0, 2)}9${digitos.slice(2)}`;
    } else if (digitos.length === 11) {
      digitos = `55${digitos}`;
    } else if (digitos.length === 12 && digitos.startsWith('55')) {
      digitos = `${digitos.slice(0, 4)}9${digitos.slice(4)}`;
    }
    return digitos;
  }

  deletarAnexo(){
    this.anexoArquivo = undefined;
    this.envioAnexo.emit(this.anexoArquivo);
    this.colunasComplementaresAtualizadas.emit([]);
  }

  validarCpf(cpf: string): boolean {
    return MethodsUtils.validarCPFCNPJ(cpf);
  }
}
